import { ReportReason } from '$shared/constants/reportReason';
import { ColorsEnum } from '$shared/constants/colors';
import { AttachBodyItem } from '$store/attach/interface';
import { LivePermissionEnum } from '$store/live/interface';

export enum UserRegByEnum {
  GOOGLE = 'g',
  EMAIL = 'e',
  TELEGRAM = 'tg',
  TELEGRAM_BOT = 'tgbot',
}

export interface PromoLinkModel {
  id: number;
  name: string;
  description: string;
  slug: string;
  token: string;
  utmSource: string;
  targetPath: string;
  webUrl: string;
  miniAppUrl: string;
  visits: number;
  createdAt: number;
}

export interface TrialLinkModel {
  id: number;
  name: string;
  hash: string;
  limit: number;
  activated: number;
  createdAt: number;
  activeAt?: number;
  period: number;
}

export interface UserModel {
  id: number;
  firstName: string;
  lastName: string;
  bDate: string;
  sex: Sex;
  countryId: number;
  countryName: string;
  cityId: number;
  cityName: string;
  lang?: string;
  photoId?: number;
  thumbnail: string;
  photo: string;
  photoMedium: string;
  photoLarge: string;
  followers: number;
  followed: number;
  lastUpdate: number;
  status: string;
  invites?: number;
  isAnimeEditor?: boolean;
  domain?: string;
  verified: number;
  rating: number;
  isBanned: boolean;
  bio: string;
  availCountries?: string[];
  notifySettings?: number;
  hasNftPhoto: boolean;
  hasSubscription: boolean;
  isSponsor?: boolean;
  hiddenSex?: number;
  isTelegramUser: boolean;
  shadowBan?: boolean;
  botFactor?: number;
  monetization?: boolean;
  inviterId?: number;
  secondaryInviter?: number;
  manualFactor?: number;
  boostedTo?: number;
  penalizedUntil?: number;
  spamBot?: boolean;
  regBy?: UserRegByEnum;
  email?: string;
  login?: string;
  banReason?: string;
  isolated: boolean;
  allowWriteForFree: boolean;
  ai: boolean;
  adminNote?: string;
  isService?: boolean;
  aiCaps?: AdminAICaps;
  passKey?: boolean;
}

export interface AdminAICaps {
  suggest: boolean;
  suggestModel?: string;
  suggestAbout?: string;
  suggestExtras?: string;
  spendLevel?: boolean;
}

export const AI_SUGGEST_MODELS = [
  'x-ai/grok-4.3',
  'x-ai/grok-4.1-fast',
  'x-ai/grok-3-mini',
  'openai/gpt-4o-mini',
  'openai/gpt-3.5-turbo',
] as const;

export type AiSuggestModel = typeof AI_SUGGEST_MODELS[number];

export interface TransactionModel {
  id: number;
  userId: number;
  amount: number;
  currency: string;
  paid: boolean;
  metadata: string;
  providerTxId: number;
  paymentSystem: string;
  countryCode: string;
  topUpAmount: number;
  createdAt: number;
}

export interface PurchaseMessages {
  modelId: number;
  userId: number;
  all: string[];
  unlocked: string[];
  paid: string[];
  notPaid: string[];
  sent: string[];
  received: string[];
  content: Messages;
}

export interface Messages {
  [key: string]: Message;
}

export interface Message {
  messageId: number;
  isHidden: boolean;
  attach: AttachItem;
}

export interface AccountModel {
  id: number;
  updatesCount: number;
}

export interface AccountAuthModel {
  id: number;
  updatedAt: number;
  token: string;
}

export interface Permissions {
  report: boolean;
  finance: boolean;
  users: boolean;
  ads: boolean;
  utm: boolean;
  messages: boolean;
  translation: boolean;
  battle: boolean;
  migrateAccount: boolean;
  topUpManual: boolean;
  topUpWelcomeBonus: boolean;
  withdrawal: boolean;
  changePassword: boolean;
  auditLog: boolean;
  apiTokens: boolean;
  moderationAdmin: boolean;
}

export enum ReportObjectType {
  POST = 'post',
  COMMENT = 'comment',
  MESSAGE = 'message',
  REEL = 'reels',
}

export interface ReportModel {
  id: number;
  userId: number;
  createdAt: number;
  objectId: number;
  objectType: ReportObjectType;
  reason: ReportReason;
  moderId: number;
  resolved: boolean;
  isDeleted: boolean;
}

export enum ReportStatus {
  BANNED = 'banned',
  PENDING = 'pending',
  RESOLVED = 'resolved',
  DELETED = 'deleted',
}

export interface PostReplyTo {
  postId: number;
  ownerId: number;
}

export enum PostType {
  FREE = 1,
  SUBSCRIPTION = 2,
  PRIVATE_SUBSCRIPTION = 3,
}

export interface PromoPostModel {
  id: number;
  ownerId: number;
  text: string;
  attachments: AttachItem[];
  createdAt: number;
}

export interface PostModel {
  id: number;
  ownerId: number;
  publisherId: number;
  text: string;
  createdAt: number;
  updatedAt: number;
  parentId: number;
  parentOwnerId?: number;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  isLiked: boolean;
  attachments?: AttachItem[];
  isDeleted?: boolean;
  canDelete: boolean;
  replyTo?: PostReplyTo;
  level: number;
  pinned: boolean;
  type: PostType;
  sponsored?: boolean;
  isAdult?: boolean;
  tips: number;
  adId: number;
  translations: { [index: string]: string };
}

export enum AttachType {
  PHOTO = 'photo',
  LINK = 'link',
  VIDEO = 'video',
  TIPS = 'tips',
  POLL = 'poll',
  ROULETTE = 'roulette',
  ROULETTE_LOT = 'rouletteLot',
  BATTLE = 'battlePrize',
  BUNDLE = 'bundle',
  VALENTINE_DAY_STICKER = 'valentine_day_sticker',
  GIFT = 'gift',
  SERVICE_NOTIFICATION = 'service_notification',
  SERVICE_SUMMARY = 'service_summary',
}

export interface AttachServiceNotification {
  serviceId: number;
  fromUserId: number;
}

export interface AttachServiceSummary {
  serviceId: number;
  count: number;
}

export interface AttachItem {
  type: AttachType;
  photo?: AttachPhoto;
  video?: AttachVideo;
  link?: AttachLink;
  tips?: AttachTips;
  poll?: AttachPoll;
  roulette?: AttachRoulette;
  bundle?: AttachBundle;
  rouletteLot?: AttachRouletteLot;
  battlePrize?: AttachBattlePrize;
  gift?: AttachGift;
  serviceNotification?: AttachServiceNotification;
  serviceSummary?: AttachServiceSummary;
}

export interface AttachPhoto {
  isVideo?: boolean;
  photoMedium: string;
  photoLarge: string;
  width: number;
  height: number;
  photoId: number;
  thumbnail?: string;
  thumbHash?: string;
  photoMediumEncrypted?: string;
  photoLargeEncrypted?: string;
  photoFile?: File;
  encrypted?: boolean;
}

export interface AttachVideo {
  photoMedium: string;
  photoLarge: string;
  width: number;
  height: number;
  videoId: number;
  encrypted?: boolean;
  source: string;
  duration?: number;
  thumbnail: string;
  thumbHash?: string;
}

export interface AttachUploadData {
  bucket: string;
  header: Headers;
  key: string;
  publicUrl: string;
  url: string;
  width: number;
  height: number;
  duration: number;
  pickerId: string;
}

export type AttachUploadStatus =
  | 'uploading'
  | 'waiting_network'
  | 'retrying'
  | 'failed'
  | 'saving'
  | 'save_failed';

export interface AttachUploadError {
  kind: string;
  message?: string;
}

export interface AttachUploadingVideo {
  video: File | null;
  loaded: number;
  total: number;
  isLoaded: boolean;
  isCancelled: boolean;
  pickerId: string;
  uploadData?: AttachUploadData;
  asPeerId?: number;
  status?: AttachUploadStatus;
  attempt?: number;
  error?: AttachUploadError;
  uploadId?: string;
}

export interface AttachLink {
  type: string;
  object: any;
  extra?: any;
}

export interface AttachTips {
  id: number;
  fromId: number;
  toId: number;
  amount: number;
  currency: string;
  postId: number;
}

export interface PollOption {
  label: string;
  answers: number;
}

export interface AttachPoll {
  pollId: number;
  title: string;
  options: PollOption[];
  anonymous: boolean;
  multipleAnswer: boolean;
  correctAnswer: number;
  voted?: number[];
  stopped: boolean;
  totalVotes: number;
}

export interface AttachRouletteLot {
  title: string;
  index: number;
  color: ColorsEnum;
  postId?: number;
  description?: string;
}

export interface AttachBattlePrize {
  battleId: number;
  modelOne: UserModel;
  modelTwo: UserModel;
}

export interface AttachGift {
  giftId: number;
  url: string;
  type: GiftType;
}

export interface AttachBundle {
  bundle: BundleModel;
  currency: CurrencyEnum;
  price: number;
}

export interface AttachRoulette {
  rouletteId: number;
  price: number;
  counter: number;
  lots: AttachRouletteLot[];
}

export interface AnimeGenreModel {
  id: number;
  title: string;
}

export interface AnimeThemeModel {
  id: number;
  title: string;
}

export interface AnimeModel {
  id: number;
  title: string;
  originalTitle: string;
  description: string;
  photos: string[];
  genres: AnimeGenreModel[];
  themes: AnimeThemeModel[];
  years: string[];
  seasons: number;
  episodes: number;
  likes: number;
  isLiked: boolean;
  ageRestriction: number;
}

export interface AnimeEpisodeModel {
  id: number;
  videoId: number;
  translationId: number;
  season: number;
  number: number;
  type: number;
  isFiller: boolean;
}

export interface VideoModel {
  id: number;
  poster: string;
  source: { resolution: number; path: string }[];
  thumbnail: string;
}

export interface AnimeTranslationModel {
  id: number;
  name: string;
  lang: string;
}

export interface AnimeStateModel {
  translationId: number;
  season: number;
  episode: number;
}

export interface AnimeRecentModel {
  anime: AnimeModel;
  info?: AnimeStateModel;
}

export interface SearchResultModel {
  type: string;
  objectId: number;
  isFollowed: boolean;
  cover?: string;
  coverThumbnail?: string;
}

export interface FollowersResultModel {
  type: string;
  objectId: number;
}

export interface GroupModel {
  id: number;
  name: string;
  description: string;
  photoId?: number;
  photo: string;
  photoMedium: string;
  photoLarge: string;
  followers: number;
  isModer: boolean;
  isAdmin: boolean;
  status: string;
  link: string;
  adultOnly: boolean;
  forceAdult: boolean;
  domain?: string;
  verified: number;
  isBanned: boolean;
}

export interface GroupAdmin {
  userId: number;
  role: string;
}

export interface DialogModel {
  id: number;
  peerId: number;
  unread: number;
  lastMsgId: number;
  modelLastMsgId: number;
  createdAt: number;
  sponsor: boolean;
  score: number;
}

export enum MessageTelegramStatus {
  Sending = 0,
  Received = 1,
  Failed = 2,
}

export interface MessageReplyToModel {
  id: number;
  fromId: number;
  text?: string;
  attachments?: AttachItem[];
  isDeleted?: boolean;
}

export interface MessageModel {
  id: number;
  fromId: number;
  toId: number;
  text: string;
  attachments?: AttachItem[];
  createdAt: number;
  replyToId?: number;
  replyTo?: MessageReplyToModel;
  isSending?: boolean;
  isFailed?: boolean;
  isInbox: boolean;
  isUnread?: boolean;
  isHidden?: boolean;
  isWelcome?: boolean;
  isBroadcast?: boolean;
  isDeleted?: boolean;
  price?: number;
  priceWithoutDiscount?: number;
  decryptKey?: string;
  currency?: string;
  telegramStatus?: MessageTelegramStatus;
  idempotencyKey?: string;
}

export enum NotifyModelType {
  // real values
  FOLLOW = 'follow',
  FOLLOW_GROUPING = 'follow_grouping',
  LIKE = 'like',
  COMMENT = 'comment',
  TIPS = 'tips',
  SOLD_MESSAGES = 'sold_messages',
  NEW_SUBSCRIBERS = 'new_subscribers',
  NEW_DEVICE = 'new_device',
  ENDED_SUBSCRIPTION = 'ended_subscription',
  SUBSCRIPTION_WAS_ENDING_SOON = 'subscription_ending_soon',
  ROULETTE_MESSAGES = 'roulette_messages',
  BUNDLE_SOLD = 'bundle_sold',
  GIFT_SOLD = 'gift_sold',

  // custom frontend types
  UNDEFINED = 'undefined',
  ALL = 'all',
  SALES = 'sales',
  SYSTEM = 'system',
  POST = 'post',
}

export interface NotifyModel {
  id: number;
  type: NotifyModelType;
  group: string;
  extra: any;
  createdAt: number;
}

export interface AnimeListsModel {
  id: number;
  title: string;
  description: string;
  animeIds: number[];
  createAt: number;
  privacy: number;
  ownerId: number;
}

export interface FeedNotificationModel {
  id: string;
  title: string;
  description: string;
  iconUrl: string;
  actionText: string;
  actionUrl: string;
  action: string;
  actionParams: { [key: string]: any };
  style?: 'default' | 'blue' | 'purple' | 'pink' | 'red';
}

export enum BalanceOperationType {
  MineSpend = 'mine_spend',
  BuyTon = 'buy_ton',
  AdsWithdraw = 'ads_withdraw',
  AdsRefill = 'ads_refill',
  ExchangeToTons = 'exchange_to_tons',
  ExchangeFromEur = 'exchange_from_eur',
  TopUp = 'top_up',
  TopUpEur = 'top_up_eur',
  TopUpManual = 'top_up_manual',
  CashBack = 'cashback',
  AdjustmentNegative = 'adjustment_negative',
  TopUpWelcomeBonus = 'top_up_welcome_bonus',
  SubscriptionProfit = 'subscription_profit',
  ReferralProfit = 'referral_profit',
  ReferralProfitMessage = 'referral_profit_message',
  ReferralProfitTips = 'referral_profit_tips',
  ReferralProfitBundle = 'referral_profit_bundle',
  ReferralProfitSubscription = 'referral_profit_subscription',
  ReferralProfitSponsorSubscription = 'referral_profit_sponsor_subscription',
  Profit = 'profit',
  ProfitAds = 'profit_ads',
  BuySponsorSubscription = 'buy_sponsor_subscription',
  BuySponsorSubscriptionAutoCharge = 'buy_sponsor_subscription_auto_charge',
  BuySubscription = 'buy_subscription',
  BuyTrialSubscription = 'buy_trial_subscription',
  BuyCandySubscription = 'buy_candy_subscription',
  BuySubscriptionAutoCharge = 'buy_subscription_auto_charge',
  BuyMessage = 'buy_message',
  SoldMessage = 'sold_message',
  Refund = 'refund',
  SendTips = 'send_tips',
  ReceiveTips = 'receive_tips',
  Staking = 'staking',
  StakeProfit = 'stake_profit',
  BattleDonate = 'battle_donate',
  BattleReward = 'battle_reward',
  Contest = 'contest',
  HeyfansPayout = 'heyfans_payout',
  BuyTelegramPost = 'buy_telegram_post',
  SellTelegramPost = 'sell_telegram_post',
  RefundToBalance = 'refund_to_balance',
  TypeBuyBundle = 'buy_bundle',
  TypeSoldBundle = 'sold_bundle',
  CancelFunds = 'cancel_funds',
  TypeTaskReward = 'task_reward',
  OfferShare = 'offer_share',
  MineExploreCard = 'mine_explore_card',

  // legacy
  Withdraw = 'withdraw',
  Withdrawal = 'withdrawal',
}

export interface BalanceOperationModel {
  id: number;
  operation: BalanceOperationType;
  createdAt: number;
  amount: string;
  extra: string;
  status: string;
  blockchainTransaction: BlockchainTransactionModel | null;
  blockchainHash: string | null;
  currency: string;
  rolledBack: boolean;
}

export interface BlockchainTransactionModel {
  hash: string;
  addressFrom: string;
  addressTo: string;
  amount: number;
  fee: number;
  lt: number;
  payload: string;
  signature: number;
}

export enum WithdrawalModelStatus {
  NotApproved = 'not_approved',
  Pending = 'pending',
  Sending = 'sending',
  Completed = 'completed',
  Failed = 'failed',
  Canceled = 'canceled',
  RolledBack = 'rolled_back',
}
export interface WithdrawalModel {
  id: number;
  amount: string;
  userId: number;
  status: WithdrawalModelStatus;
  createdAt: number;
  blockchainTransaction: BlockchainTransactionModel | null;
  blockchainHash: string | null;
  address: string;
}

export interface InvitedModel {
  id: number;
  createdAt: number;
  userId: number;
  balance: number;
}

export interface AgentStatModel {
  id: number;
  createdAt: number;
  userId: number;
  amount: number;
  operation: string;
}

export interface BscAddressModel {
  address: string;
  expireAt: number;
}

export interface ExploreTagModel {
  name: string;
  count: number;
}

export interface PriceModel {
  ton: number;
  eur: number;
}

export enum SubscriptionType {
  Adult = 1,
  Media = 2,
  Author = 3,
  Basic = 4,
}

export interface SubscriptionModel {
  id: number;
  type: SubscriptionType;
  isActive: boolean;
  activeAt: number;
  authorId: number;
  price: number;
  currency: string;
  period: number;
  isSuspended?: boolean;
  nextChargeAt?: number;
}

export interface StatisticsItemModel {
  date: number;
  total: number;
  currency: CurrencyEnum;
}

export enum StatisticsEarningsTypes {
  MEMBERSHIP = 'membership',
  SPONSORS = 'sponsors',
  MESSAGES = 'messages',
  REFERRALS = 'referrals',
  STAKING = 'staking',
  TIPS = 'tips',
  BUNDLES = 'bundles',
}

export type StatisticsEarningsModel = {
  [key in StatisticsEarningsTypes]: number;
};

export type StatisticsContents = {
  [date: number]: {
    [key in CurrencyEnum]: StatisticsEarningsModel & { currency: CurrencyEnum };
  };
};

export type StatisticsContentModel = {
  isLoading: boolean;
  earnings: StatisticsContents;
};
export type PaidMessageModel = MessageModel & {
  userPhoto: string;
  userName: string;
};

export interface SessionModel {
  id: number;
  type: string;
  ip: string;
  device: string;
  lastUpdate: number;
  country: string;
  isCurrent: boolean;
}

export interface NftModel {
  address: string;
  collection_address: string;
  index: number;
  init: boolean;
  metadata: {
    attributes: null;
    content_type: string;
    content_url: string;
    description: string;
    image: string;
    name: string;
  };
  owner: {
    address: string;
  };
  raw_individual_content: string;
  verified: boolean;
}

export interface LinkModel {
  id: number;
  text: string;
  url: string;
  color: string;
  icon: string;
}

export interface ViewPost {
  postId: number;
  mediaViewCount?: number;
  postViewDuration?: number;
  mediaViewDuration?: number;
}

export enum CurrencyEnum {
  EUR = 'eur',
  USD = 'usd',
  TON = 'ton',
  PLACE = 'place',
}

export interface TipsModel {
  id: number;
  amount: number;
  fromId: number;
  toId: number;
  message: string;
  currency: CurrencyEnum;
  postId: number;
}

export interface CloudAlbumModel {
  id: number;
  created_at: string;
  title: string;
  coverUrl: string;
  isDefault: boolean;
}

interface CloudAlbumContentModel {
  id: number;
  createdAt: string;
  elementId: number;
  path: string;
  albumId: number;
  bought: boolean;
}

type CloudAlbumPhotoModel = {
  elementType: 'photo';
  photoExtra?: {
    width: number;
    height: number;
  };
} & CloudAlbumContentModel;

type CloudAlbumVideoModel = {
  elementType: 'video';
  videoExtra?: {
    posterId: number;
    posterPath: string;
    duration: number;
    width: number;
    height: number;
  };
} & CloudAlbumContentModel;

export type CloudAlbumItemModel = CloudAlbumPhotoModel | CloudAlbumVideoModel;

export enum CloudMediaTabs {
  ALL = 'all',
  PHOTO = 'photo',
  VIDEO = 'video',
}

export enum CloudDefaultAlbums {
  UPLOADED = 'uploaded',
  MESSAGES = 'messages',
  POSTS = 'posts',
}

export interface StoryModel {
  id: number;
  userId: number;
  createdAt: number;
  type: 'video' | 'photo';
  thumbnail: string;
  thumbHash?: string;
  mediaUrl: string;
  unread: boolean;
  views?: number;
  duration: number;
  actionButton?: {
    action: string;
    payload: string;
  };
}

export interface StoriesGroup {
  userId: number;
  stories: StoryModel[];
}

export interface StoriesView {
  userId: number;
  createdAt: number;
}

export enum AdStatus {
  none = 0,
  paused = 1,
  active = 2,
  inReview = 3,
  declined = 4,
  approved = 5,
}

export interface AdModel {
  name: string;
  id: number;
  price: number;
  spendLimit: number;
  totalSpend: number;
  createdAt: number;
  status: AdStatus;
  conversions: number;
  objectType: 'profile' | 'promo_post' | 'reels' | 'post';
  objectId: number;
  targetSubscribed: boolean;
  targetLang: string;
  userId: number;
  showToConverted: boolean;
  currency: string;
}

export interface AdStatsModel {
  date: string;
  spend: number;
  total: number;
}

export interface TableAdsModel {
  id: number;
  object: {
    id: number;
    type: string;
    adId: number;
  };
  userId: number;
  status: AdStatus;
  money: {
    price: number;
    limit: number;
    balance: number;
    totalSpend: number;
  };
  actions: {
    status: AdStatus;
    adId: number;
  };
}

export enum GenderFilterEnum {
  NONE = 0,
  WOMEN = 1,
  MEN = 2,
  BOTH = 3,
}

export interface UserProfileSubscriptionModel {
  activeAt: number;
  authorId: number;
  category: SubscriptionType;
  createdAt: number;
  currency: CurrencyEnum;
  id: number;
  price: number;
  retries: number;
  subscriptionId: number;
  tkInvoiceId: string;
  tonPrice: number;
  type: string;
  updatedAt: number;
  userId: number;
  // Приостановлена после неудачного автосписания (TPP-262)
  suspendedAt?: number;
  nextChargeAt?: number;
}

export type VerificationStatus =
  | 'not_verified'
  | 'in_verification'
  | 'rejected'
  | 'ready_to_kyc'
  | 'in_kyc'
  | 'kyc_approved'
  | 'verified';

export interface UserProfileVerificationModel {
  createdAt: number;
  links?: {
    platform: string;
    username: string;
    raw: string;
    url: string;
  }[];
  link?: string;
  status: VerificationStatus;
  sumSub: string;
  verifiedAt: number;
  about: string;
  verificationId: number;
  topModel: number;
  highPercent: boolean;
}

export interface AdminProfileUserModel {
  common: {
    ip: string[];
    lang: string;
    registration: number;
    user: UserModel;
    balance: number;
    balances: {
      eur: number;
      ton: number;
      ads?: number;
      eur_hold?: number;
    };
    eurBalance: number;
    countryCode: string;
  };
  balanceOperations: BalanceOperationModel[];
  receivedReports: {
    notResolved: number;
    resolved: number;
  };
  referrals: {
    invited: InvitedModel;
    user: UserModel;
  }[];
  subscribersCount: number;
  subscriptions: UserProfileSubscriptionModel[];
  verification: UserProfileVerificationModel;
  withdrawals: WithdrawalModel[];
}

export enum AdminProfileUserDataCategories {
  USER_COMMON = 'userCommon',
  VERIFICATION = 'verification',
  BALANCE_OPERATIONS = 'balanceOperations',
}

export interface AuditLogModel {
  id: number;
  createdAt: number;
  userId: number;
  method: string;
  path: string;
  requestId: string;
  ip: string;
  body?: string;
  meta?: Record<string, any>;
  statusCode: number;
  duration: number;
}

export type TableParamsModel = Partial<{
  page: number;
  pageSize: number;
  searchFilter: string;
  filterTab: string;
}>;

export interface ListInfoModel {
  count: number;
  listId: string;
  title: string;
  userID: number[];
  custom: boolean;
}

export interface ListsUserInfo {
  Id: number;
  avatar: string;
  name: string;
}

export interface ListsAdditionalUserInfo {
  purchasedCount: number;
  purchasedTotal: number;
  subscription: {
    createdAt: number;
    price: number;
    duration: number;
    expiredAt: number;
  };
  subscriptionCount?: number;
  tipsCount: number;
  tipsTotal: number;
  total: number;
}

export enum AuthPopoverState {
  LOGIN = 'login',
  REGISTER = 'register',
}

export interface ImportedModule {
  [key: string]: {
    default: string;
  };
}

export interface BattleDonateModel {
  id: number;
  userId: number;
  amount: number;
  createdAt: number;
  toUserId: number;
  anonymous: boolean;
}

export interface BattlePromotedModel {
  id: number;
  description: string;
  endAt: number;
  oneAmount: number;
  twoAmount: number;
  onePhoto: string;
  twoPhoto: string;
}

export interface Battle {
  id: number;
  oneUserId: number;
  twoUserId: number;
  endAt: number;
}

export interface TranslationsLanguage {
  ID: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  name: string;
  code: string;
  editors: null;
  translator: number | null;
}

export interface TranslationsTranslationModel {
  ID: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  langId: number;
  key: string;
  translator: number | null;
  description: string;
  zero: string;
  one: string;
  two: string;
  few: string;
  many: string;
  other: string;
}

export enum TranslationsLanguagePlatforms {
  FRONTEND = 'frontend',
  BACKEND = 'backend',
}

export enum ExportTranslationsFileTypes {
  JSON = 'json',
  TXT = 'txt',
}

export interface FeedModelRow {
  userId: number;
  content: AttachItem[];
  videos: number;
  photos: number;
}

export interface TelegramChat {
  id: number;
  title: string;
  chatId: number;
  link: string;
  isPrivate: boolean;
}

export interface TelegramPost {
  price: number;
  message: string;
  chatIds: number[];
  attachments: AttachItem[];
  lang: string;
  disableNotifications: boolean;
}

export interface Lot {
  id: number;
  title: string;
  color: ColorsEnum;
  description: string;
  attachments: AttachBodyItem[];
}

export interface AppModel {
  id: number;
  name: string;
  description: string;
  url: string;
  photo: string;
  originalUrl: string;
}

export interface SubscriptionRateModel {
  period: number;
  price: number;
}

export interface StreamModel {
  id: number;
  status: 'active' | 'finished';
  streamerId: number;
  type: LivePermissionEnum;
  createdAt: number;
  srs: string;
}

export enum StreamMessageModelType {
  StreamMessageText = 'text',
  StreamMessageJoined = 'user_joined',
  StreamMessageLeft = 'user_left',
  StreamUserBanned = 'user_banned',
  StreamUserUnbanned = 'user_unbanned',
}

export interface StreamMessageModel {
  id: number;
  fromId: number;
  streamId: number;
  createdAt: number;
  text: string;
  type: StreamMessageModelType;
  attachments: AttachItem[];
}

export interface ReelsModel {
  id: number;
  ownerId: number;
  likes: number;
  views: number;
  isLiked: boolean;
  isDeleted: boolean;
  videoUrl: string;
  poster: string;
  photosCount: number;
  videosCount: number;
}

export interface BundleModel {
  id: number;
  name: string;
  description: string;
  previews: AttachItem[];
  content: AttachItem[];
  userId: number;
  calendarId?: number;
  price: number;
  priceWithoutDiscount: number;
  currency: string;
  coverUrl: string;
  thumbnail: string;
}

export enum DashboardTaskActionEnum {
  ROUTE = 'route',
  MODAL = 'modal',
  WRITE_TO_USER = 'write_to_users',
  SET_COVER = 'set_cover',
  SET_PHOTO = 'set_photo',
  COPY_INVITE_LINK = 'copy_invite_link',
}

export interface DashboardTaskModel {
  id: number;
  type: 'fatal' | 'warning' | 'info';
  title: string;
  reward: number;
  text: string;
  fullText: string;
  currentValue: number;
  finalValue: number;
  action?: {
    type: DashboardTaskActionEnum;
    routeName?: string;
    modalOptions?: any;
    routeParams?: any;
    options: number[];
    modalName?: string;
    buttonText: string;
  };
  isActive: boolean;
  isReached: boolean;
  isCompleted: boolean;
  isRecurring: boolean;
}

export interface DashboardGroupModel {
  tasks: DashboardTaskModel[];
}

export interface DashboardSectionModel {
  name: string;
  icon: string;
  groups: DashboardGroupModel[];
}

export enum Sex {
  UNKNOWN = 0,
  FEMALE = 1,
  MALE = 2,
}

export interface DashboardNotifyModel {
  title: string;
  text: string;
  color: string;
  link: string;
}

export interface OfferModel {
  id: number;
  userId: number;
  sharePercent: number;
  shareType: number;
  content: AttachItem[];
  description: string;
  conversionRate: number;
  sharePeriodDays: number;
  isDeleted: boolean;
  canShare: boolean;
}

export interface OfferStatModel {
  views: number;
  registers: number;
  profit: number;
}

export interface SystemPromoCodeModel {
  id: number;
  title: string;
  description: string;
  createdAt: number;
  code: string;
}

export interface CoinSquad {
  id: number;
  userId: number;
  type: string;
  url: string;
  data: string;
  membersCount: number;
  earnAmount: number;
  level: number;
  boost: number;
}

export interface CoinSquadMember {
  coinSquadId: number;
  userId: number;
  earnAmount: number;
}

export interface CoinBoostsTask {
  id: string;
  reward: number;
  title: string;
  claimed: boolean;
}

export interface MineFriend {
  userId: number;
  coins: number;
  active: boolean;
}

export enum ModerationTypeEnum {
  all = 0,
  post = 1,
  reel = 2,
  avatar = 3,
  cover = 4,
  bundle = 5,
  welcomeVideo = 6,
  status = 7,
  postEdit = 8,
  bio = 9,
  links = 10,
}

export enum ModerationStatusEnum {
  all = 0,
  pending = 1,
  approved = 2,
  declined = 3,
  hold = 4,
  remoderation = 5,
}

export interface ModerationModel {
  id: number;
  objectId: number;
  objectType: ModerationTypeEnum;
  status: ModerationStatusEnum;
  ownerId: number;
  moderatorId: number;
  createdAt: number;
}

export enum MineSpendTypeEnum {
  FeeReduction = 'fee_reduction',
}

export interface MineSpend {
  id: number;
  type: MineSpendTypeEnum;
  period: number;
  extra: string;
  activeAt: number;
}

export interface MineSquadBoostModel {
  name: string;
  minSquadLevel: number;
  price: number;
  bonus: number;
  curLevel: number;
  maxLevels: number;
  title: string;
  description: string;
  smile: string;
}

export enum MineExploreCardContentTypeEnum {
  photo = 1,
  video = 2,
}

export interface MineExploreCard {
  id: number;
  ownerId: number;
  level: number;
  price: number;
  contentType: MineExploreCardContentTypeEnum;
  contentPath: string;
  contentImageUrl: string;
  contentId: number;
  thumb: string;
  isBought: boolean;
  contentWidth: number;
  contentHeight: number;
}

export enum GiftType {
  Animated = 'animated',
  Static = 'static',
}

export interface GiftModel {
  id: number;
  url: string;
  price: number;
  type: GiftType;
}

export interface WonLeaderboard {
  id: number;
  date: number;
  startAt: number;
  endAt: number;
  rank: number;
}
